class DisplayItemHelper {
    public static readonly EDIT_VIEW = 'edit'
    public static readonly SHOW_EDIT_VIEW = 'show/edit'
    public static readonly SHOW_VIEW = 'show'
    public static readonly LIST_VIEW = 'list'

    public static readonly FIELD_CUSTOMACTIONBTN_TYPE = 'customActionButton'
    public static readonly FIELD_LIST_TYPE = 'list'
    public static readonly FIELD_TEXTAREA_TYPE = 'textarea'
    public static readonly FIELD_DATE_TYPE = 'date'
    public static readonly FIELD_TIME_TYPE = 'time'
    public static readonly FIELD_AMOUNT_TYPE = 'amount'

    public static readonly SYSTEM_SORT_FIELDS = [
        'created_at',
        'modified_at',
        'created_by',
        'modified_by',
        'dossier_id',
    ]

    static isValidSortFieldId(value: any) {
        if (value === null || value === undefined || value === '') return false

        const fieldId = Number(value)
        return Number.isInteger(fieldId) && fieldId > 0
    }

    static normalizeSortSettings(sortSettings: any = []) {
        if (!Array.isArray(sortSettings)) return []

        const normalized = new Map()

        sortSettings.forEach((sortSetting) => {
            if (!sortSetting?.sort) return

            const dossierField =
                DisplayItemHelper.SYSTEM_SORT_FIELDS.find(
                    (field) => sortSetting[field] === true
                ) ??
                (DisplayItemHelper.SYSTEM_SORT_FIELDS.includes(
                    sortSetting.dddfId
                )
                    ? sortSetting.dddfId
                    : null)

            if (dossierField) {
                normalized.set(`system:${dossierField}`, {
                    [dossierField]: true,
                    sort: sortSetting.sort,
                })
                return
            }

            if (!DisplayItemHelper.isValidSortFieldId(sortSetting.dddfId)) {
                return
            }

            const fieldId = Number(sortSetting.dddfId)

            normalized.set(`field:${fieldId}`, {
                dddfId: fieldId,
                sort: sortSetting.sort,
            })
        })

        return Array.from(normalized.values())
    }

    static sortSettingsAreEqual(left: any, right: any) {
        if (!Array.isArray(left) || !Array.isArray(right)) return left === right
        if (left.length !== right.length) return false

        return left.every((leftItem, index) => {
            const rightItem = right[index]
            const leftKeys = Object.keys(leftItem ?? {}).sort()
            const rightKeys = Object.keys(rightItem ?? {}).sort()

            return (
                leftKeys.length === rightKeys.length &&
                leftKeys.every(
                    (key, keyIndex) =>
                        key === rightKeys[keyIndex] &&
                        leftItem[key] === rightItem[key]
                )
            )
        })
    }

    /**
     * Explicitly clear mutually exclusive keys so recursive PATCH mergers
     * cannot retain stale field IDs or system flags from stored array items.
     */
    static serializeSortSettingsForPatch(sortSettings: any = []) {
        return DisplayItemHelper.normalizeSortSettings(sortSettings).map(
            (sortSetting) => {
                const serialized: any =
                    DisplayItemHelper.SYSTEM_SORT_FIELDS.reduce(
                        (result, field) => ({ ...result, [field]: false }),
                        {
                            dddfId: null,
                            sort: sortSetting.sort,
                        }
                    )
                const dossierField = DisplayItemHelper.SYSTEM_SORT_FIELDS.find(
                    (field) => sortSetting[field] === true
                )

                if (dossierField) {
                    serialized[dossierField] = true
                } else {
                    serialized.dddfId = sortSetting.dddfId
                }

                return serialized
            }
        )
    }

    static mapSortSettings(sortSettings: any) {
        return DisplayItemHelper.normalizeSortSettings(sortSettings).map(
            (sortSetting) => {
                const dossierField = DisplayItemHelper.SYSTEM_SORT_FIELDS.find(
                    (field) => sortSetting[field] === true
                )

                if (dossierField) {
                    return {
                        order: sortSetting.sort,
                        item: {
                            settings: {
                                dossier_data: dossierField,
                            },
                        },
                    }
                }

                return {
                    order: sortSetting.sort,
                    item: {
                        def_dossier_def_field: {
                            id: sortSetting.dddfId,
                        },
                    },
                }
            }
        )
    }

    static filterViews = (
        items: Array<IDisplayField>,
        views: Array<string>
    ) => {
        return items.filter((item) => views.includes(item.view))
    }

    static isTypeOfField = (
        field: IDisplayField,
        fieldType: string
    ): boolean => {
        if (
            fieldType === 'link' &&
            field.def_dossier_link &&
            field.def_dossier_link.id > 0
        ) {
            return true
        } else if (
            field.def_dossier_def_field &&
            field.def_dossier_def_field.def_field &&
            field.def_dossier_def_field.def_field.type === fieldType
        ) {
            return true
        }

        return false
    }

    static isObligatory = (item: IDisplayField): boolean => {
        if (
            item.def_dossier_def_field &&
            item.def_dossier_def_field.obligatory &&
            item.def_dossier_def_field.obligatory === true
        ) {
            return true
        }

        if (
            item.def_dossier_link &&
            item.def_dossier_link.obligatory === true
        ) {
            return true
        }

        return false
    }
}

export default DisplayItemHelper
